import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import LocationPicker from "../components/LocationPicker";
import "./css/AddWork.css";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;

const MAX_DOCUMENTS = 5;
const MAX_FILE_SIZE = 5 * 1096000; // ~5MB per requirements

const ALLOWED_DOC_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const EMIRATES = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
];

const formatDate = (date) => date.toISOString().split("T")[0];

export default function AddWork() {
  const navigate = useNavigate();
  const location = useLocation();
  const editWork = location.state?.editWork || null;
  const documentInputRef = useRef(null);

  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [specialisations, setSpecialisations] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [subCategorySearch, setSubCategorySearch] = useState("");
  const [selectedSpecialisations, setSelectedSpecialisations] = useState([]);
  const [specialisationSearch, setSpecialisationSearch] = useState("");

  const [title, setTitle] = useState("");
  const [quoteLastDate, setQuoteLastDate] = useState("");
  const [description, setDescription] = useState("");

  const [sameAsProfile, setSameAsProfile] = useState(true);
  const [profile, setProfile] = useState(null);
  const [hasAppliedProfileAddress, setHasAppliedProfileAddress] = useState(false);

  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");

  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const [workAmountMin, setWorkAmountMin] = useState("");
  const [workAmountMax, setWorkAmountMax] = useState("");

  const [documents, setDocuments] = useState([]);
  const [errors, setErrors] = useState({});

  const titleFieldRef = useRef(null);
  const categoryFieldRef = useRef(null);
  const subCategoriesFieldRef = useRef(null);
  const specialisationsFieldRef = useRef(null);
  const quoteDateFieldRef = useRef(null);
  const descriptionFieldRef = useRef(null);
  const workAmountFieldRef = useRef(null);
  const address1FieldRef = useRef(null);
  const address2FieldRef = useRef(null);
  const cityFieldRef = useRef(null);
  const stateFieldRef = useRef(null);
  const pincodeFieldRef = useRef(null);
  const locationFieldRef = useRef(null);

  // Listen for language changes triggered elsewhere
  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("app_language") || "en");
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const t = useMemo(
    () =>
      ({
        en: {
          pageTitle: "Add Work",
          heading: "Basic Details",
          categories: "Categories",
          subCategories: "Sub Categories",
          specialisations: "Specialisations",
          searchSubCategories: "Search Sub Categories",
          searchSpecialisations: "Search Specialisations",
          lastDate: "Last Date To Submit Quote",
          selectDate: "Click Here to Select",
          description: "Work Description",
          descriptionPlaceholder: "Add the work description here...",
          documents: "Work Documents",
          documentsInfo:
            "Max 5 documents allowed. Supported formats: PNG, JPG, JPEG, SVG, WebP, AVIF, HEIC, PDF, Word",
          clickToUpload: "Click Here To Upload",
          locationDetails: "Location Details",
          sameAsProfile: "Same as profile address",
          address1: "Address Line 1",
          address2: "Address Line 2",
          city: "City",
          state: "Emirate",
          selectState: "Click Here to Select",
          pincode: "PO Box",
          locationOnMap: "Location On Map",
          selectLocation: "Click Here to Select",
          locationSelected: "Location Selected",
          workAmount: "Work Amount (AED)",
          minAmount: "Minimum Amount",
          maxAmount: "Maximum Amount",
          title: "Work Title",
          titlePlaceholder: "Enter work title",
          selectedSubCategories: "Selected Sub Categories",
          selectedSpecialisations: "Selected Specialisations",
          addWork: "Add Work",
          loading: "Loading...",
          noCategories: "No categories available",
          categoryRequired: "Please select a category",
          subCategoryRequired: "Select at least one sub category",
          specialisationRequired: "Select at least one specialisation",
          titleRequired: "Title is required",
          titleLength: "Title must be between 1 and 50 characters",
          quoteDateRequired: "Quote submission date is required",
          quoteDateInvalid: "Select a future date within the next 30 days",
          descriptionRequired: "Description is required",
          descriptionLength: "Description must be between 1 and 1000 characters",
          addressRequired: "This field is required",
          cityRequired: "City is required",
          stateRequired: "Please select an emirate",
          pincodeRequired: "PO Box is required",
          pincodeInvalid: "Please enter a valid PO Box.",
          locationRequired: "Location is required",
          workAmountRequired: "Enter minimum and maximum amount",
      workAmountInvalid: "Amounts must be between 0 and 10,000,000",
      workAmountRange: "Maximum amount must be greater than or equal to minimum amount",
          documentTypeError:
            "File format should be PNG, JPG, JPEG, SVG, WebP, AVIF, HEIC, PDF, WORD",
          documentSizeError: "Each file must be less than 5MB",
          maxDocuments: "Maximum 5 documents allowed",
          submitSuccess: "Work added successfully!",
          submitError: "Failed to add work",
        },
        ar: {
          pageTitle: "أضف عملاً",
          heading: "التفاصيل الأساسية",
          categories: "الفئات",
          subCategories: "الفئات الفرعية",
          specialisations: "التخصصات",
          searchSubCategories: "ابحث عن فئة فرعية",
          searchSpecialisations: "ابحث عن تخصص",
          lastDate: "آخر موعد لتقديم العرض",
          selectDate: "انقر هنا للاختيار",
          description: "وصف العمل",
          descriptionPlaceholder: "أضف وصف العمل هنا...",
          documents: "مستندات العمل",
          documentsInfo:
            "الحد الأقصى 5 ملفات. التنسيقات المدعومة: PNG, JPG, JPEG, SVG, WebP, AVIF, HEIC, PDF, WORD",
          clickToUpload: "انقر هنا للتحميل",
          locationDetails: "تفاصيل الموقع",
          sameAsProfile: "مثل عنوان الملف الشخصي",
          address1: "سطر العنوان 1",
          address2: "سطر العنوان 2",
          city: "المدينة",
          state: "الإمارة",
          selectState: "انقر للاختيار",
          pincode: "ص.ب",
          locationOnMap: "الموقع على الخريطة",
          selectLocation: "انقر هنا للاختيار",
          locationSelected: "تم اختيار الموقع",
          workAmount: "قيمة العمل (درهم)",
          minAmount: "الحد الأدنى",
          maxAmount: "الحد الأقصى",
          title: "عنوان العمل",
          titlePlaceholder: "أدخل عنوان العمل",
          selectedSubCategories: "الفئات الفرعية المختارة",
          selectedSpecialisations: "التخصصات المختارة",
          addWork: "أضف العمل",
          loading: "جاري التحميل...",
          noCategories: "لا توجد فئات متاحة",
          categoryRequired: "يرجى اختيار فئة",
          subCategoryRequired: "يرجى اختيار فئة فرعية واحدة على الأقل",
          specialisationRequired: "يرجى اختيار تخصص واحد على الأقل",
          titleRequired: "العنوان مطلوب",
          titleLength: "يجب أن يتراوح العنوان بين 1 و50 حرفاً",
          quoteDateRequired: "مطلوب تاريخ تقديم العرض",
          quoteDateInvalid: "اختر تاريخاً مستقبلياً خلال 30 يوماً القادمة",
          descriptionRequired: "الوصف مطلوب",
          descriptionLength: "يجب أن يكون الوصف بين 1 و1000 حرف",
          addressRequired: "هذه الخانة مطلوبة",
          cityRequired: "المدينة مطلوبة",
          stateRequired: "يرجى اختيار الإمارة",
          pincodeRequired: "ص.ب مطلوب",
          pincodeInvalid: "يرجى إدخال صندوق بريد صالح.",
          locationRequired: "الموقع مطلوب",
          workAmountRequired: "يرجى إدخال الحد الأدنى والأقصى للمبلغ",
      workAmountInvalid: "يجب أن تكون المبالغ بين 0 و10,000,000",
          workAmountRange: "يجب أن يكون الحد الأقصى أكبر من أو يساوي الحد الأدنى",
          documentTypeError:
            "يجب أن يكون تنسيق الملف PNG أو JPG أو JPEG أو SVG أو WebP أو AVIF أو HEIC أو PDF أو WORD",
          documentSizeError: "يجب أن يكون حجم كل ملف أقل من 5 ميجابايت",
          maxDocuments: "الحد الأقصى 5 ملفات",
          submitSuccess: "تمت إضافة العمل بنجاح!",
          submitError: "فشل في إضافة العمل",
        },
      }[lang]),
    [lang]
  );

  // Fetch categories and profile
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
          showToast("Unauthorized. Please login again.", "error");
          navigate("/");
          return;
        }

        const [categoriesData, profileData] = await Promise.allSettled([
          apiFetch(`${USER_BASE_URL}/${VERSION}/sub-category/`, "GET", null, {}),
          apiFetch(`${USER_BASE_URL}/${VERSION}/profile/`, "GET", null, {}),
        ]);

        if (isMounted && categoriesData.status === "fulfilled") {
          const response = categoriesData.value;
          if (response?.response) {
            const payload = response.response;
            if (Array.isArray(payload)) {
              setCategories(payload);
            } else {
              setCategories(payload.categories || []);
              setSpecialisations(payload.specialisations || []);
            }
          }
        }

        if (isMounted && profileData.status === "fulfilled") {
          const response = profileData.value;
          if (response?.response) {
            setProfile(response.response);
          }
        }
      } catch (error) {
        console.error("Failed to load data for add work:", error);
        if (error.message !== "AccessDenied") {
          showToast(error.message || t.submitError, "error");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [navigate, t.submitError]);

  // Apply profile details when checkbox is selected
  useEffect(() => {
    if (!profile || !sameAsProfile) {
      return;
    }

    setAddress1(profile.address1 || "");
    setAddress2(profile.address2 || "");
    setCity(profile.city || "");
    setState(profile.state || "");
    setPincode(
      profile.pincode !== undefined && profile.pincode !== null
        ? String(profile.pincode)
        : ""
    );

    if (
      Array.isArray(profile.location_coordinates) &&
      profile.location_coordinates.length === 2
    ) {
      // Profile location_coordinates is [longitude, latitude]
      const [lng, lat] = profile.location_coordinates;
      setLatitude(Number(lat));
      setLongitude(Number(lng));
    }
    // If profile doesn't have location coordinates, keep existing values
    // User will need to manually select location if not already set

    setHasAppliedProfileAddress(true);
  }, [profile, sameAsProfile]);

  // Populate form when editing work
  useEffect(() => {
    if (!editWork || !categories.length) return;

    // Set title
    if (editWork.title) setTitle(editWork.title);

    // Set category and sub categories
    if (editWork.category_id) {
      const catId = typeof editWork.category_id === "object" 
        ? editWork.category_id._id 
        : editWork.category_id;
      setSelectedCategoryId(catId);
      
      if (editWork.sub_categories && Array.isArray(editWork.sub_categories)) {
        const subCatIds = editWork.sub_categories.map(sc => 
          typeof sc === "object" ? sc._id : sc
        );
        setSelectedSubCategories(subCatIds);
      }
    }

    // Set quote last date
    if (editWork.quote_last_date) {
      const date = new Date(editWork.quote_last_date);
      setQuoteLastDate(formatDate(date));
    }

    // Set description
    if (editWork.description) setDescription(editWork.description);

    // Set work amount
    if (editWork.work_amount) {
      if (editWork.work_amount.min !== undefined) {
        setWorkAmountMin(String(editWork.work_amount.min));
      }
      if (editWork.work_amount.max !== undefined) {
        setWorkAmountMax(String(editWork.work_amount.max));
      }
    }

    // Set address (don't use profile address when editing)
    setSameAsProfile(false);
    if (editWork.address1) setAddress1(editWork.address1);
    if (editWork.address2) setAddress2(editWork.address2);
    if (editWork.city) setCity(editWork.city);
    if (editWork.state) setState(editWork.state);
    if (editWork.pincode !== undefined && editWork.pincode !== null) {
      setPincode(String(editWork.pincode));
    }

    // Set location
    if (editWork.location_coordinates && Array.isArray(editWork.location_coordinates) && editWork.location_coordinates.length === 2) {
      setLatitude(Number(editWork.location_coordinates[1]));
      setLongitude(Number(editWork.location_coordinates[0]));
    }
  }, [editWork, categories]);

  const today = new Date();
  const minDate = formatDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1));
  const maxDate = formatDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30));

  // Check if profile has complete address data
  const isProfileAddressComplete = useMemo(() => {
    if (!profile) return false;
    const hasAddress1 = profile.address1 && profile.address1.trim().length > 0;
    const hasAddress2 = profile.address2 && profile.address2.trim().length > 0;
    const hasCity = profile.city && profile.city.trim().length > 0;
    const hasState = profile.state && EMIRATES.includes(profile.state);
    const hasPincode = profile.pincode !== undefined && profile.pincode !== null && 
      !Number.isNaN(Number(profile.pincode)) && Number(profile.pincode) > 0;
    const hasLocation = Array.isArray(profile.location_coordinates) && 
      profile.location_coordinates.length === 2;
    
    return hasAddress1 && hasAddress2 && hasCity && hasState && hasPincode && hasLocation;
  }, [profile]);

  const activeCategory = useMemo(
    () => categories.find((cat) => cat._id === selectedCategoryId),
    [categories, selectedCategoryId]
  );

  const filteredSubCategories = useMemo(() => {
    if (!activeCategory?.sub_categories) return [];
    if (!subCategorySearch.trim()) return activeCategory.sub_categories;

    const query = subCategorySearch.toLowerCase();
    return activeCategory.sub_categories.filter((subCat) =>
      subCat.name?.toLowerCase().includes(query)
    );
  }, [activeCategory, subCategorySearch]);

  const filteredSpecialisations = useMemo(() => {
    if (!specialisations || specialisations.length === 0) return [];
    if (!specialisationSearch.trim()) return specialisations;
    const query = specialisationSearch.toLowerCase();
    return specialisations.filter((spec) =>
      spec.name?.toLowerCase().includes(query)
    );
  }, [specialisations, specialisationSearch]);

  const toggleSubCategory = (subCategoryId) => {
    setSelectedSubCategories((prev) =>
      prev.includes(subCategoryId)
        ? prev.filter((id) => id !== subCategoryId)
        : [...prev, subCategoryId]
    );
  };

  const removeSubCategory = (subCategoryId) => {
    setSelectedSubCategories((prev) => prev.filter((id) => id !== subCategoryId));
  };

  const toggleSpecialisation = (specId) => {
    setSelectedSpecialisations((prev) =>
      prev.includes(specId) ? prev.filter((id) => id !== specId) : [...prev, specId]
    );
  };

  const removeSpecialisation = (specId) => {
    setSelectedSpecialisations((prev) => prev.filter((id) => id !== specId));
  };

  const validateDocument = (file) => {
    if (!ALLOWED_DOC_TYPES.includes(file.type)) {
      const validExtensions = [
        ".png",
        ".jpg",
        ".jpeg",
        ".svg",
        ".webp",
        ".avif",
        ".heic",
        ".heif",
        ".pdf",
        ".doc",
        ".docx",
      ];
      const lowerName = file.name.toLowerCase();
      const hasValidExtension = validExtensions.some((ext) => lowerName.endsWith(ext));
      if (!hasValidExtension) {
        return { valid: false, error: t.documentTypeError };
      }
    }

    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: t.documentSizeError };
    }

    return { valid: true };
  };

  const handleDocumentSelect = (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) return;

    if (documents.length + files.length > MAX_DOCUMENTS) {
      showToast(t.maxDocuments, "error");
      return;
    }

    const validFiles = [];
    files.forEach((file) => {
      const validation = validateDocument(file);
      if (validation.valid) {
        validFiles.push(file);
      } else if (validation.error) {
        showToast(validation.error, "error");
      }
    });

    if (validFiles.length) {
      const remainingSlots = MAX_DOCUMENTS - documents.length;
      setDocuments((prev) => [...prev, ...validFiles.slice(0, remainingSlots)]);
    }

    if (documentInputRef.current) {
      documentInputRef.current.value = "";
    }
  };

  const removeDocument = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLocationSelect = (lat, lng) => {
    setLatitude(Number(lat));
    setLongitude(Number(lng));
  };

  const handleSameAsProfileToggle = (checked) => {
    setSameAsProfile(checked);
    // Clear validation errors when toggling
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.address1;
      delete newErrors.address2;
      delete newErrors.city;
      delete newErrors.state;
      delete newErrors.pincode;
      delete newErrors.location;
      return newErrors;
    });
    
    if (checked && profile) {
      setHasAppliedProfileAddress(false);
    } else if (!checked && profile && hasAppliedProfileAddress) {
      setAddress1("");
      setAddress2("");
      setCity("");
      setState("");
      setPincode("");
      setLatitude(null);
      setLongitude(null);
      setHasAppliedProfileAddress(false);
    }
  };

  const scrollToFirstError = (errorMap) => {
    const fieldRefs = {
      title: titleFieldRef,
      category: categoryFieldRef,
      subCategories: subCategoriesFieldRef,
      specialisations: specialisationsFieldRef,
      quoteLastDate: quoteDateFieldRef,
      description: descriptionFieldRef,
      workAmount: workAmountFieldRef,
      address1: address1FieldRef,
      address2: address2FieldRef,
      city: cityFieldRef,
      state: stateFieldRef,
      pincode: pincodeFieldRef,
      location: locationFieldRef,
    };

    for (const key of Object.keys(errorMap)) {
      const ref = fieldRefs[key];
      if (ref?.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        const focusable = ref.current.querySelector(
          "input, select, textarea, button"
        );
        if (focusable && typeof focusable.focus === "function") {
          focusable.focus({ preventScroll: true });
        }
        break;
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    const trimmedAddress1 = address1.trim();
    const trimmedAddress2 = address2.trim();
    const trimmedCity = city.trim();

    if (!trimmedTitle) {
      newErrors.title = t.titleRequired;
    } else if (trimmedTitle.length > 50) {
      newErrors.title = t.titleLength;
    }

    if (!selectedCategoryId) {
      newErrors.category = t.categoryRequired;
    }

    if (!selectedSubCategories.length) {
      newErrors.subCategories = t.subCategoryRequired;
    }

    if (!selectedSpecialisations.length) {
      newErrors.specialisations = t.specialisationRequired;
    }

    if (!quoteLastDate) {
      newErrors.quoteLastDate = t.quoteDateRequired;
    } else if (quoteLastDate <= formatDate(today) || quoteLastDate < minDate || quoteLastDate > maxDate) {
      newErrors.quoteLastDate = t.quoteDateInvalid;
    }

    if (!trimmedDescription) {
      newErrors.description = t.descriptionRequired;
    } else if (trimmedDescription.length > 1000) {
      newErrors.description = t.descriptionLength;
    }

    if (!trimmedAddress1) {
      newErrors.address1 = t.addressRequired;
    } else if (trimmedAddress1.length > 255) {
      newErrors.address1 = t.addressRequired;
    }

    if (!trimmedAddress2) {
      newErrors.address2 = t.addressRequired;
    } else if (trimmedAddress2.length > 255) {
      newErrors.address2 = t.addressRequired;
    }

    if (!trimmedCity) {
      newErrors.city = t.cityRequired;
    } else if (trimmedCity.length > 100) {
      newErrors.city = t.cityRequired;
    }

    if (!state) {
      newErrors.state = t.stateRequired;
    } else if (!EMIRATES.includes(state)) {
      newErrors.state = t.stateRequired;
    }

    if (!pincode.trim()) {
      newErrors.pincode = t.pincodeRequired;
    } else {
      const numeric = Number(pincode);
      if (Number.isNaN(numeric) || numeric <= 0) {
        newErrors.pincode = t.pincodeInvalid;
      }
    }

    if (latitude === null || longitude === null) {
      newErrors.location = t.locationRequired;
    }

    const minAmount = parseFloat(workAmountMin);
    const maxAmount = parseFloat(workAmountMax);

    if (Number.isNaN(minAmount) || Number.isNaN(maxAmount)) {
      newErrors.workAmount = t.workAmountRequired;
    } else if (
      minAmount < 0 ||
      maxAmount < 0 ||
      minAmount > 10000000 ||
      maxAmount > 10000000
    ) {
      newErrors.workAmount = t.workAmountInvalid;
    } else if (maxAmount < minAmount) {
      newErrors.workAmount = t.workAmountRange;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => {
        scrollToFirstError(newErrors);
      }, 0);
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    setSubmitLoading(true);

    try {
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("sub_categories", JSON.stringify(selectedSubCategories));
      formData.append("specialisations", JSON.stringify(selectedSpecialisations));
      formData.append("quote_last_date", quoteLastDate);
      formData.append("description", description.trim());
      formData.append("address1", address1.trim());
      formData.append("address2", address2.trim());
      formData.append("city", city.trim());
      formData.append("pincode", Number(pincode));
      formData.append("state", state);
      formData.append("location_coordinates", JSON.stringify([Number(longitude), Number(latitude)])); // DB format: [longitude, latitude]
      formData.append(
        "work_amount",
        JSON.stringify({
          min: parseFloat(workAmountMin),
          max: parseFloat(workAmountMax),
        })
      );

      documents.forEach((file) => {
        formData.append("file", file);
      });

      const response = await fetch(`${USER_BASE_URL}/${VERSION}/work/add`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          "app-version": "1.0.0",
        },
        body: formData,
      });

      const data = await response.json();

      if (data && data.response_code === 200) {
        showToast(t.submitSuccess, "success");
        navigate("/dashboard");
      } else {
        showToast(data?.message || t.submitError, "error");
      }
    } catch (error) {
      console.error("Error adding work:", error);
      showToast(error.message || t.submitError, "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const renderSelectedSubCategories = () => {
    if (!selectedSubCategories.length) return null;

    return (
      <div className="selected-subcategories-container">
        <h4 className="section-subheading">{t.selectedSubCategories}</h4>
        <div className="selected-subcategories-chips">
          {selectedSubCategories.map((subCatId) => {
            const subCat = activeCategory?.sub_categories?.find((sc) => sc._id === subCatId);
            if (!subCat) return null;
            return (
              <span key={subCatId} className="selected-subcategory-chip">
                {subCat.name}
                <button type="button" onClick={() => removeSubCategory(subCatId)} aria-label="Remove">
                  ×
                </button>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSelectedSpecialisations = () => {
    if (!selectedSpecialisations.length) return null;

    return (
      <div className="selected-subcategories-container">
        <h4 className="section-subheading">{t.selectedSpecialisations}</h4>
        <div className="selected-subcategories-chips">
          {selectedSpecialisations.map((specId) => {
            const spec = specialisations.find((s) => s._id === specId);
            if (!spec) return null;
            return (
              <span key={specId} className="selected-subcategory-chip">
                {spec.name}
                <button
                  type="button"
                  onClick={() => removeSpecialisation(specId)}
                  aria-label="Remove"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="add-work-loading" dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  return (
    <div className="add-work-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Header lang={lang} />

      <div className="add-work-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="add-work-page-title">{t.pageTitle}</h1>
        <div className="header-spacer"></div>
      </div>

      <div className="add-work-content">
        <form onSubmit={handleSubmit} noValidate>
          <section className="form-section">
            <h2 className="section-heading">{t.heading}</h2>

            <div ref={titleFieldRef} className="form-field">
              <label className="form-label">{t.title}</label>
              <input
                type="text"
                className={`form-input ${errors.title ? "error" : ""}`}
                placeholder={t.titlePlaceholder}
                value={title}
                maxLength={50}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) setErrors((prev) => ({ ...prev, title: null }));
                }}
              />
              {errors.title && <span className="error-message">{errors.title}</span>}
            </div>

            <div ref={categoryFieldRef} className="form-field">
              <label className="form-label">{t.categories}</label>
              {categories.length ? (
                <div className="category-chips">
                  {categories.map((category) => (
                    <button
                      key={category._id}
                      type="button"
                      className={`category-chip ${
                        selectedCategoryId === category._id ? "selected" : ""
                      }`}
                      onClick={() => {
                        setSelectedCategoryId((prev) =>
                          prev === category._id ? prev : category._id
                        );
                        if (selectedCategoryId !== category._id) {
                          setSelectedSubCategories([]);
                        }
                        if (errors.category) {
                          setErrors((prev) => ({ ...prev, category: null }));
                        }
                      }}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="form-info">{t.noCategories}</p>
              )}
              {errors.category && <span className="error-message">{errors.category}</span>}
            </div>

            {selectedCategoryId && (
              <div ref={subCategoriesFieldRef} className="form-field">
                <label className="form-label">{t.subCategories}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={t.searchSubCategories}
                  value={subCategorySearch}
                  onChange={(e) => setSubCategorySearch(e.target.value)}
                />

                {renderSelectedSubCategories()}

                <div className="subcategories-list">
                  {filteredSubCategories.map((subCat) => {
                    const isSelected = selectedSubCategories.includes(subCat._id);
                    return (
                      <label key={subCat._id} className={`sub-category-item ${isSelected ? "selected" : ""}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            toggleSubCategory(subCat._id);
                            if (errors.subCategories) {
                              setErrors((prev) => ({ ...prev, subCategories: null }));
                            }
                          }}
                        />
                        <span>{subCat.name}</span>
                      </label>
                    );
                  })}
                </div>
                {errors.subCategories && (
                  <span className="error-message">{errors.subCategories}</span>
                )}
              </div>
            )}

            {specialisations.length > 0 && (
              <div ref={specialisationsFieldRef} className="form-field">
                <label className="form-label">{t.specialisations}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={t.searchSpecialisations}
                  value={specialisationSearch}
                  onChange={(e) => setSpecialisationSearch(e.target.value)}
                />

                {renderSelectedSpecialisations()}

                <div className="subcategories-list">
                  {filteredSpecialisations.map((spec) => {
                    const isSelected = selectedSpecialisations.includes(spec._id);
                    return (
                      <label
                        key={spec._id}
                        className={`sub-category-item ${isSelected ? "selected" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            toggleSpecialisation(spec._id);
                            if (errors.specialisations) {
                              setErrors((prev) => ({ ...prev, specialisations: null }));
                            }
                          }}
                        />
                        <span>{spec.name}</span>
                      </label>
                    );
                  })}
                </div>
                {errors.specialisations && (
                  <span className="error-message">{errors.specialisations}</span>
                )}
              </div>
            )}

            <div ref={quoteDateFieldRef} className="form-field">
              <label className="form-label">{t.lastDate}</label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  className={`form-input date-input ${errors.quoteLastDate ? "error" : ""}`}
                  placeholder={t.selectDate}
                  min={minDate}
                  max={maxDate}
                  value={quoteLastDate}
                  onChange={(e) => {
                    setQuoteLastDate(e.target.value);
                    if (errors.quoteLastDate) {
                      setErrors((prev) => ({ ...prev, quoteLastDate: null }));
                    }
                  }}
                />
                <i className="bx bx-calendar date-icon"></i>
              </div>
              {errors.quoteLastDate && (
                <span className="error-message">{errors.quoteLastDate}</span>
              )}
            </div>

            <div ref={descriptionFieldRef} className="form-field">
              <label className="form-label">{t.description}</label>
              <textarea
                className={`form-textarea ${errors.description ? "error" : ""}`}
                placeholder={t.descriptionPlaceholder}
                value={description}
                rows={5}
                maxLength={1000}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) {
                    setErrors((prev) => ({ ...prev, description: null }));
                  }
                }}
              />
              {errors.description && (
                <span className="error-message">{errors.description}</span>
              )}
            </div>

            <div ref={workAmountFieldRef} className="form-field">
              <label className="form-label">{t.workAmount}</label>
              <div className="work-amount-grid">
                <div className="work-amount-field">
                  <span className="work-amount-label">{t.minAmount}</span>
                  <input
                    type="number"
                    className={`form-input ${errors.workAmount ? "error" : ""}`}
                    min="0"
                    step="0.01"
                    value={workAmountMin}
                    onChange={(e) => {
                      setWorkAmountMin(e.target.value);
                      if (errors.workAmount) {
                        setErrors((prev) => ({ ...prev, workAmount: null }));
                      }
                    }}
                  />
                </div>
                <div className="work-amount-field">
                  <span className="work-amount-label">{t.maxAmount}</span>
                  <input
                    type="number"
                    className={`form-input ${errors.workAmount ? "error" : ""}`}
                    min="0"
                    step="0.01"
                    value={workAmountMax}
                    onChange={(e) => {
                      setWorkAmountMax(e.target.value);
                      if (errors.workAmount) {
                        setErrors((prev) => ({ ...prev, workAmount: null }));
                      }
                    }}
                  />
                </div>
              </div>
              {errors.workAmount && <span className="error-message">{errors.workAmount}</span>}
            </div>
          </section>

          <section className="form-section">
            <h2 className="section-heading">{t.documents}</h2>
            <p className="form-info">{t.documentsInfo}</p>

            <div className="file-upload-section">
              <input
                ref={documentInputRef}
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.svg,.webp,.avif,.heic,.heif,.pdf,.doc,.docx"
                onChange={handleDocumentSelect}
                className="file-input-hidden"
                disabled={documents.length >= MAX_DOCUMENTS}
              />

              {documents.length > 0 && (
                <div className="document-grid">
                  {documents.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="document-item">
                      <div className="document-icon">
                        {file.type.includes("pdf")
                          ? "PDF"
                          : file.type.includes("word")
                          ? "DOC"
                          : file.type.includes("png")
                          ? "PNG"
                          : file.type.includes("jpeg") || file.type.includes("jpg")
                          ? "JPG"
                          : "FILE"}
                      </div>
                      <button
                        type="button"
                        className="document-delete-btn"
                        onClick={() => removeDocument(index)}
                        aria-label="Remove document"
                      >
                        <i className="bx bx-x"></i>
                      </button>
                      <p className="document-name">
                        {file.name.length > 18 ? `${file.name.slice(0, 15)}...` : file.name}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {documents.length < MAX_DOCUMENTS && (
                <div
                  className="file-upload-area"
                  onClick={() => documentInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      documentInputRef.current?.click();
                    }
                  }}
                >
                  <p>{t.clickToUpload}</p>
                </div>
              )}
            </div>
          </section>

          <section className="form-section">
            <h2 className="section-heading">{t.locationDetails}</h2>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={sameAsProfile}
                onChange={(e) => handleSameAsProfileToggle(e.target.checked)}
              />
              <span>{t.sameAsProfile}</span>
            </label>

            <div ref={address1FieldRef} className="form-field">
              <label className="form-label">{t.address1}</label>
              <input
                type="text"
                className={`form-input ${errors.address1 ? "error" : ""}`}
                value={address1}
                maxLength={255}
                onChange={(e) => {
                  setAddress1(e.target.value);
                  if (errors.address1) setErrors((prev) => ({ ...prev, address1: null }));
                }}
                disabled={sameAsProfile && profile && hasAppliedProfileAddress && isProfileAddressComplete}
              />
              {errors.address1 && <span className="error-message">{errors.address1}</span>}
            </div>

            <div ref={address2FieldRef} className="form-field">
              <label className="form-label">{t.address2}</label>
              <input
                type="text"
                className={`form-input ${errors.address2 ? "error" : ""}`}
                value={address2}
                maxLength={255}
                onChange={(e) => {
                  setAddress2(e.target.value);
                  if (errors.address2) setErrors((prev) => ({ ...prev, address2: null }));
                }}
                disabled={sameAsProfile && profile && hasAppliedProfileAddress && isProfileAddressComplete}
              />
              {errors.address2 && <span className="error-message">{errors.address2}</span>}
            </div>

            <div ref={cityFieldRef} className="form-field">
              <label className="form-label">{t.city}</label>
              <input
                type="text"
                className={`form-input ${errors.city ? "error" : ""}`}
                value={city}
                maxLength={100}
                onChange={(e) => {
                  setCity(e.target.value);
                  if (errors.city) setErrors((prev) => ({ ...prev, city: null }));
                }}
                disabled={sameAsProfile && profile && hasAppliedProfileAddress && isProfileAddressComplete}
              />
              {errors.city && <span className="error-message">{errors.city}</span>}
            </div>

            <div ref={stateFieldRef} className="form-field">
              <label className="form-label">{t.state}</label>
              <select
                className={`form-input ${errors.state ? "error" : ""}`}
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  if (errors.state) setErrors((prev) => ({ ...prev, state: null }));
                }}
                disabled={sameAsProfile && profile && hasAppliedProfileAddress && isProfileAddressComplete}
              >
                <option value="">{t.selectState}</option>
                {EMIRATES.map((emirate) => (
                  <option key={emirate} value={emirate}>
                    {emirate}
                  </option>
                ))}
              </select>
              {errors.state && <span className="error-message">{errors.state}</span>}
            </div>

            <div ref={pincodeFieldRef} className="form-field">
              <label className="form-label">{t.pincode}</label>
              <input
                type="text"
                inputMode="numeric"
                className={`form-input ${errors.pincode ? "error" : ""}`}
                value={pincode}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  setPincode(digits);
                  if (errors.pincode) setErrors((prev) => ({ ...prev, pincode: null }));
                }}
                disabled={sameAsProfile && profile && hasAppliedProfileAddress && isProfileAddressComplete}
              />
              {errors.pincode && <span className="error-message">{errors.pincode}</span>}
            </div>

            <div
              ref={locationFieldRef}
              className={`form-field ${errors.location ? "has-error" : ""}`}
            >
              <label className="form-label">{t.locationOnMap}</label>
              <button
                type="button"
                className={`location-select-btn ${latitude !== null && longitude !== null ? "selected" : ""}`}
                onClick={() => setShowLocationPicker(true)}
              >
                {latitude !== null && longitude !== null ? t.locationSelected : t.selectLocation}
              </button>

              {latitude !== null && longitude !== null && (
                <div className="location-selected-values">
                  <span>
                    Lat: {latitude.toFixed(6)}
                  </span>
                  <span>
                    Lng: {longitude.toFixed(6)}
                  </span>
                </div>
              )}

              {errors.location && <span className="error-message">{errors.location}</span>}
            </div>
          </section>

          <button type="submit" className="add-work-submit-btn" disabled={submitLoading}>
            {submitLoading ? (
              <>
                <div className="button-spinner"></div>
                {t.loading}
              </>
            ) : (
              t.addWork
            )}
          </button>
        </form>
      </div>

      <BottomNavbar currentPage="add" lang={lang} />

      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onSelect={handleLocationSelect}
        initialLat={latitude}
        initialLng={longitude}
        lang={lang}
      />
    </div>
  );
}

